import moment from 'moment';
import 'moment/locale/pt';

moment.locale('pt');

/**
 * Export route report as PDF using jsPDF
 */
export async function exportPDF(route, vehicle, driver) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const primary = [0, 212, 255];
  const dark = [8, 13, 26];
  const gray = [100, 116, 139];
  const lightGray = [226, 232, 240];

  // Header background
  doc.setFillColor(...dark);
  doc.rect(0, 0, 210, 40, 'F');

  // Logo accent bar
  doc.setFillColor(...primary);
  doc.rect(0, 0, 4, 40, 'F');

  // Title
  doc.setTextColor(...lightGray);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('EcooGPS Planner', 12, 18);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text('Relatório de Rota', 12, 26);
  doc.text(`Gerado em ${moment().format('DD/MM/YYYY HH:mm')}`, 12, 32);

  let y = 52;

  // Route Info Section
  sectionHeader(doc, 'INFORMAÇÃO DA ROTA', y, primary);
  y += 8;

  const routeData = [
    ['Nome da Rota', route.name || '(sem nome)'],
    ['Origem', route.origin?.name || '—'],
    ['Destino', route.destination?.name || '—'],
    ['Referência de Carga', route.cargoRef || '—'],
    ['Hora de Partida', route.departureTime ? moment(route.departureTime).format('DD/MM/YYYY HH:mm') : '—'],
    ['Distância', route.computedRoute ? `${(route.computedRoute.distance / 1000).toFixed(1)} km` : '—'],
    ['Duração', route.computedRoute ? formatDuration(route.computedRoute.duration) : '—'],
  ];

  y = dataTable(doc, routeData, y, lightGray, gray);
  y += 8;

  // Vehicle & Driver
  if (vehicle || driver) {
    sectionHeader(doc, 'VEÍCULO & MOTORISTA', y, primary);
    y += 8;

    const vdData = [
      ...(vehicle ? [
        ['Matrícula', vehicle.plate],
        ['Tipo de Veículo', vehicle.type],
        ['Capacidade', vehicle.capacity],
        ['Consumo', `${vehicle.fuelPer100km} L/100km`],
      ] : []),
      ...(driver ? [
        ['Motorista', driver.name],
        ['Classe de Carta', driver.licenseClass],
        ['Cartão Tacógrafo', driver.tachoCard],
        ['Telefone', driver.phone],
      ] : []),
    ];

    y = dataTable(doc, vdData, y, lightGray, gray);
    y += 8;
  }

  // Stops
  if (route.stops && route.stops.length > 0) {
    sectionHeader(doc, `PARAGENS DE DESCANSO (${route.stops.length})`, y, primary);
    y += 8;

    doc.setFontSize(8);
    doc.setTextColor(...gray);
    doc.text('Regulamento (CE) n.º 561/2006 — Limite: 4h30 condução contínua', 15, y);
    y += 6;

    route.stops.forEach((stop, idx) => {
      if (y > 260) {
        doc.addPage();
        y = 20;
      }

      doc.setFillColor(17, 24, 39);
      doc.roundedRect(14, y, 182, 18, 2, 2, 'F');
      doc.setFillColor(...primary);
      doc.rect(14, y, 2, 18, 'F');

      doc.setTextColor(...lightGray);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Paragem ${idx + 1}`, 20, y + 6);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...gray);
      doc.text(`Km ${stop.km} · Pausa ${stop.duration}min`, 20, y + 13);

      if (stop.arrivalTime) {
        doc.text(`Chegada: ${stop.arrivalTime}`, 120, y + 10);
      }

      y += 22;
    });
    y += 4;
  } else if (route.computedRoute) {
    sectionHeader(doc, 'PARAGENS DE DESCANSO', y, primary);
    y += 8;
    doc.setFontSize(9);
    doc.setTextColor(0, 230, 118);
    doc.text('✓ Nenhuma paragem obrigatória — rota dentro do limite de 4h30', 15, y);
    y += 10;
  }

  // Footer
  doc.setFillColor(...dark);
  doc.rect(0, 287, 210, 10, 'F');
  doc.setTextColor(...gray);
  doc.setFontSize(7);
  doc.text('EcooGPS Planner · Relatório gerado automaticamente', 105, 293, { align: 'center' });

  doc.save(`ecoogps-rota-${moment().format('YYYYMMDD-HHmm')}.pdf`);
}

function sectionHeader(doc, title, y, color) {
  doc.setFillColor(...color);
  doc.rect(14, y, 2, 6, 'F');
  doc.setTextColor(...color);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 18, y + 5);
  doc.setDrawColor(...color, 40);
  doc.line(18, y + 7, 196, y + 7);
}

function dataTable(doc, rows, y, lightGray, gray) {
  rows.forEach(([label, value], i) => {
    if (i % 2 === 0) {
      doc.setFillColor(17, 24, 39);
      doc.rect(14, y, 182, 6, 'F');
    }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...gray);
    doc.text(label, 18, y + 4.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...lightGray);
    doc.text(String(value), 80, y + 4.5);
    y += 7;
  });
  return y;
}

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}min`;
}

/**
 * Export route data as Excel (xlsx)
 */
export async function exportExcel(route, vehicle, driver) {
  const XLSX = await import('xlsx');

  const wb = XLSX.utils.book_new();

  // Route summary sheet
  const summaryData = [
    ['EcooGPS Planner - Relatório de Rota'],
    [],
    ['ROTA'],
    ['Nome', route.name || ''],
    ['Origem', route.origin?.name || ''],
    ['Destino', route.destination?.name || ''],
    ['Referência Carga', route.cargoRef || ''],
    ['Hora Partida', route.departureTime ? moment(route.departureTime).format('DD/MM/YYYY HH:mm') : ''],
    ['Distância (km)', route.computedRoute ? (route.computedRoute.distance / 1000).toFixed(1) : ''],
    ['Duração', route.computedRoute ? formatDuration(route.computedRoute.duration) : ''],
    [],
    ['VEÍCULO'],
    ...(vehicle ? [
      ['Matrícula', vehicle.plate],
      ['Tipo', vehicle.type],
      ['Capacidade', vehicle.capacity],
      ['Consumo (L/100km)', vehicle.fuelPer100km],
    ] : [['(não atribuído)', '']]),
    [],
    ['MOTORISTA'],
    ...(driver ? [
      ['Nome', driver.name],
      ['Carta', driver.licenseClass],
      ['Cartão Tacho', driver.tachoCard],
      ['Telefone', driver.phone],
    ] : [['(não atribuído)', '']]),
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumo');

  // Stops sheet
  if (route.stops && route.stops.length > 0) {
    const stopsData = [
      ['Paragem', 'Tipo', 'Km', 'Condução antes (min)', 'Duração pausa (min)', 'Chegada estimada'],
      ...route.stops.map((s, i) => [
        i + 1,
        s.type === 'break_45' ? 'Pausa 45min' : s.type === 'daily_rest' ? 'Descanso Diário' : 'Pausa dividida',
        s.km,
        s.drivingMinBefore,
        s.duration,
        s.arrivalTime || '',
      ]),
    ];
    const wsStops = XLSX.utils.aoa_to_sheet(stopsData);
    XLSX.utils.book_append_sheet(wb, wsStops, 'Paragens');
  }

  XLSX.writeFile(wb, `ecoogps-rota-${moment().format('YYYYMMDD-HHmm')}.xlsx`);
}

/**
 * Build WhatsApp message with route summary
 */
export function buildWhatsAppMessage(route, vehicle, driver) {
  const lines = [
    '🚛 *EcooGPS — Plano de Rota*',
    '',
    `📍 *Origem:* ${route.origin?.name || '—'}`,
    `🏁 *Destino:* ${route.destination?.name || '—'}`,
  ];

  if (route.cargoRef) lines.push(`📦 *Carga/Guia:* ${route.cargoRef}`);
  if (route.departureTime) lines.push(`🕐 *Partida:* ${moment(route.departureTime).format('DD/MM/YYYY HH:mm')}`);

  if (route.computedRoute) {
    lines.push(`📏 *Distância:* ${(route.computedRoute.distance / 1000).toFixed(1)} km`);
    lines.push(`⏱ *Duração:* ${formatDuration(route.computedRoute.duration)}`);
  }

  if (vehicle) lines.push(`🚗 *Veículo:* ${vehicle.plate} (${vehicle.type})`);
  if (driver) lines.push(`👤 *Motorista:* ${driver.name}`);

  if (route.stops && route.stops.length > 0) {
    lines.push('');
    lines.push(`⏸ *Paragens obrigatórias (Reg. UE 561/2006):*`);
    route.stops.forEach((s, i) => {
      lines.push(`  ${i + 1}. Km ${s.km} — Pausa ${s.duration}min${s.arrivalTime ? ` (aprox. ${s.arrivalTime})` : ''}`);
    });
  } else if (route.computedRoute) {
    lines.push('');
    lines.push('✅ Nenhuma paragem obrigatória necessária');
  }

  // Add navigation link
  if (route.destination?.coordinates) {
    const [dLng, dLat] = route.destination.coordinates;
    let navUrl = `https://www.google.com/maps/dir/`;
    if (route.origin?.coordinates) {
      navUrl += `${route.origin.coordinates[1]},${route.origin.coordinates[0]}/`;
    }
    (route.waypoints || []).filter(w => w.coordinates).forEach(wp => {
      navUrl += `${wp.coordinates[1]},${wp.coordinates[0]}/`;
    });
    navUrl += `${dLat},${dLng}`;
    lines.push('');
    lines.push(`🗺 *Navegação:* ${navUrl}`);
  }

  lines.push('');
  lines.push('_Enviado via EcooGPS Planner_');

  return lines.join('\n');
}

/**
 * Build SMS message (shorter version for SMS character limits)
 */
export function buildSMSMessage(route) {
  const lines = [
    'EcooGPS Plano de Rota',
    `De: ${route.origin?.name || '-'}`,
    `Para: ${route.destination?.name || '-'}`,
  ];
  if (route.computedRoute) {
    lines.push(`Dist: ${(route.computedRoute.distance / 1000).toFixed(0)}km`);
  }
  if (route.departureTime) {
    lines.push(`Partida: ${moment(route.departureTime).format('DD/MM HH:mm')}`);
  }
  // Navigation link
  if (route.destination?.coordinates) {
    const [dLng, dLat] = route.destination.coordinates;
    let navUrl = `https://www.google.com/maps/dir/`;
    if (route.origin?.coordinates) navUrl += `${route.origin.coordinates[1]},${route.origin.coordinates[0]}/`;
    (route.waypoints || []).filter(w => w.coordinates).forEach(wp => {
      navUrl += `${wp.coordinates[1]},${wp.coordinates[0]}/`;
    });
    navUrl += `${dLat},${dLng}`;
    lines.push(navUrl);
  }
  return lines.join('\n');
}
